import React from 'react';
import {
  Badge,
  Amount,
  Box,
  Button,
  InfoIcon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TablePagination,
  TableRow,
  TableToolbar,
  Text,
  EditIcon,
  ArrowRightIcon,
} from '@razorpay/blade/components';
import { ClaimsListViewProps } from './types';
import { getTableItemCountLimits } from 'components/SalaryComponents/utils';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { getStatusBadgeColor, getStatusLabel, formatDate, isPartiallyApproved } from './utils';
import { BladeTooltipWrapper } from 'components/ui/BladeTooltipWrapper/BladeTooltipWrapper';
import { routePaths } from 'components/Routes/data';
import { useHistory } from 'react-router-dom';
import { paiseAmountToRupee } from 'utils/Numbers';
import { useSearchParams } from 'hooks/useSearchParams';

export const ClaimsListView = ({
  data,
  isLoading,
  isRefreshing,
  isError,
  headers,
  currentPage,
  itemsPerPage,
  setCurrentPage,
  setItemsPerPage,
  onRowClick,
  selectedFilter,
  onRefetch,
  totalCount,
  dataUpdatedAt,
}: ClaimsListViewProps) => {
  const isTableEmpty = data.length === 0;
  const history = useHistory();
  const { urlParams } = useSearchParams();
  const userId = urlParams.get('userId');

  return (
    <>
      <Table
        data={{ nodes: data.map((claim) => ({ ...claim, id: claim.claim_id })) }}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        key={dataUpdatedAt}
        pagination={
          !isTableEmpty ? (
            <TablePagination
              paginationType="server"
              currentPage={currentPage}
              showPageSizePicker={true}
              onPageChange={({ page }) => {
                setCurrentPage(page);
              }}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
              }}
              totalItemCount={totalCount}
            />
          ) : undefined
        }
        toolbar={
          <TableToolbar
            title={getTableItemCountLimits(
              totalCount,
              currentPage,
              itemsPerPage,
              'FBP Reimbursement',
            ).replace('components', '')}
          />
        }
        showBorderedCells={false}>
        {(claims) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                {headers.map((header) => (
                  <TableHeaderCell key={header.name}>
                    <Text weight="semibold">{header.name}</Text>
                  </TableHeaderCell>
                ))}
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {claims.map((claim) => {
                const isPartiallyPaidClaim = isPartiallyApproved(claim.claim_status);
                const isStatusPending = claim.claim_status === 'SUBMITTED';
                return (
                  <TableRow
                    key={claim.claim_id}
                    item={claim}
                    onClick={() => onRowClick(claim.claim_id)}
                    testID={`claim-row-${claim.claim_id}`}
                    hoverActions={
                      <Button
                        variant="tertiary"
                        color="white"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isStatusPending) {
                            history.push(
                              {
                                pathname: routePaths.flexibleBenefits.claims.edit,
                                search: `claimId=${claim.claim_id}&userId=${
                                  userId?.toString() || ''
                                }`,
                              },
                              {
                                claimId: claim.claim_id,
                              },
                            );
                          } else {
                            onRowClick(claim.claim_id);
                          }
                        }}
                        icon={isStatusPending ? EditIcon : ArrowRightIcon}
                        iconPosition={isStatusPending ? 'left' : 'right'}>
                        {isStatusPending ? 'Modify claim' : 'View detail'}
                      </Button>
                    }>
                    <TableCell>
                      <Text>{formatDate(claim.created_at)}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{claim.component_display_name}</Text>
                    </TableCell>
                    <TableCell>
                      <Badge color={getStatusBadgeColor(claim.claim_status)}>
                        {getStatusLabel(claim.claim_status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Amount value={Number(paiseAmountToRupee(claim.claim_amount).toString())} />
                    </TableCell>
                    <TableCell>
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        gap="spacing.2"
                        justifyContent="space-between">
                        <Box display="flex" flexDirection="row" alignItems="center" gap="spacing.2">
                          {isPartiallyPaidClaim && (
                            <BladeTooltipWrapper
                              content="Amount is partially approved by your admin"
                              placement="top">
                              <InfoIcon size="small" color="surface.icon.gray.muted" />
                            </BladeTooltipWrapper>
                          )}
                          {claim.approved_amount ? (
                            <Amount
                              value={Number(paiseAmountToRupee(Number(claim.approved_amount)))}
                            />
                          ) : (
                            <Text>-</Text>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </>
        )}
      </Table>
      {isTableEmpty && !isLoading && !isRefreshing && !isError && (
        <EmptyListView
          title={
            selectedFilter !== 'all' ? `No ${selectedFilter} claims found` : 'No claims created yet'
          }
          description={
            <Text color="surface.text.gray.muted" weight="medium" textAlign="center">
              {selectedFilter !== 'all'
                ? `There are no claims with ${selectedFilter} status`
                : 'Click on Create new Claim button to create your first claim'}
            </Text>
          }>
          <Box display="flex" flexDirection="column" gap="spacing.4" marginX="auto">
            <Box marginX="auto">
              <Button
                size="medium"
                onClick={() => {
                  history.push({
                    pathname: routePaths.flexibleBenefits.claims.create,
                    search: `userId=${userId?.toString() || ''}`,
                  });
                }}>
                Create new Claim
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
      {isError && (
        <EmptyListView
          title="There was an error while fetching claims data"
          description="Please try again later">
          <Box display="flex" flexDirection="column" gap="spacing.4" marginX="auto">
            <Box marginX="auto">
              <Button size="medium" onClick={onRefetch}>
                Retry
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
    </>
  );
};
